import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import BufferIntegration from './BufferIntegration';
import SocialPosting from './SocialPosting';
import ContentProduction from './ContentProduction';

const ACCENT = '#2B7FFF';
const SURFACE = '#0B1120';
const CARD = '#0F1929';
const BORDER = 'rgba(255,255,255,0.07)';
const OFF_WHITE = '#A0AABB';
const GREEN = '#10b981';
const AMBER = '#f59e0b';

export default function ProfessionalDashboard() {
  const [stats, setStats] = useState({ weekRevenue: 0, weekAppointments: 0, completionRate: 0, returningClients: 0 });
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [analyticsRes, apptRes] = await Promise.all([api.get('/analytics'), api.get('/appointments')]);
        const t = analyticsRes.data.totals || {};
        setStats({
          weekRevenue: parseFloat(t.weekly_revenue || 0),
          weekAppointments: parseInt(t.confirmed_count || 0),
          completionRate: parseInt(t.completion_rate || 0),
          returningClients: analyticsRes.data.retention?.returning_clients || 0,
        });
        setUpcomingAppointments(
          apptRes.data
            .filter(a => new Date(a.appointment_date + 'T' + a.start_time) > new Date() && a.status === 'confirmed')
            .slice(0, 5)
        );
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'services', label: 'Services' },
    { id: 'profile', label: 'My Profile' },
    { id: 'clients', label: 'Clients' },
    { id: 'social', label: 'Social Media' },
    { id: 'content', label: 'Content' },
  ];

  if (loading) return (
    <div style={{ minHeight: '100vh', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
      <span style={{ color: OFF_WHITE, fontSize: '14px' }}>Loading...</span>
    </div>
  );

  return (
    <div style={{ background: 'transparent', minHeight: '100vh', color: '#fff', fontFamily: 'Inter, sans-serif' }}>
      {/* HEADER */}
      <div style={{ borderBottom: `1px solid ${BORDER}`, padding: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: SURFACE }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 900, margin: 0, letterSpacing: '-0.02em' }}>Dashboard</h1>
          <p style={{ color: OFF_WHITE, fontSize: '12px', margin: '2px 0 0' }}>Manage your business</p>
        </div>
        <Link to="/pro/profile" style={{ padding: '10px 18px', background: ACCENT, color: '#fff', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', fontWeight: 700 }}>
          View My Page →
        </Link>
      </div>

      <div style={{ padding: '28px 32px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* STATS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '28px' }}>
          <StatCard label="This Week" value={`$${stats.weekRevenue.toFixed(0)}`} sub="Revenue" />
          <StatCard label="Confirmed" value={stats.weekAppointments} sub="This week" />
          <StatCard label="Completion" value={`${stats.completionRate}%`} sub={stats.completionRate >= 80 ? 'Excellent' : 'Needs attention'} color={stats.completionRate >= 80 ? GREEN : AMBER} />
          <StatCard label="Returning" value={stats.returningClients} sub="Loyal clients" color={GREEN} />
        </div>

        {/* TABS */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: `1px solid ${BORDER}` }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setSelectedTab(t.id)} style={{
              padding: '12px 20px', background: 'transparent', border: 'none',
              color: selectedTab === t.id ? '#fff' : '#555',
              fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              borderBottom: selectedTab === t.id ? `2px solid ${ACCENT}` : '2px solid transparent',
              letterSpacing: '0.03em',
            }}>
              {t.label}
            </button>
          ))}
        </div>

        {selectedTab === 'overview' && <OverviewTab appointments={upcomingAppointments} />}
        {selectedTab === 'services' && <ServicesTab />}
        {selectedTab === 'profile' && <ProfileTab />}
        {selectedTab === 'clients' && <ClientsTab />}
        {selectedTab === 'social' && <SocialMediaTab />}
        {selectedTab === 'content' && <ContentProduction />}
      </div>
    </div>
  );
}

/* ─── STAT CARD ─── */
function StatCard({ label, value, sub, color = ACCENT }) {
  return (
    <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '20px' }}>
      <div style={{ color: '#444', fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>{label}</div>
      <div style={{ color, fontWeight: 900, fontSize: '28px', lineHeight: 1, marginBottom: '4px' }}>{value}</div>
      <div style={{ color: '#555', fontSize: '11px' }}>{sub}</div>
    </div>
  );
}

/* ─── OVERVIEW TAB ─── */
function OverviewTab({ appointments }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
      <div>
        <h2 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '14px' }}>Upcoming Appointments</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {appointments.length === 0 ? (
            <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '28px', textAlign: 'center', color: '#444' }}>
              No upcoming appointments
            </div>
          ) : appointments.map(a => (
            <div key={a.id} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '14px' }}>{a.client_name}</div>
                <div style={{ color: '#555', fontSize: '12px', marginTop: '2px' }}>{a.service_name} · {new Date(a.appointment_date).toLocaleDateString()} at {a.start_time}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: ACCENT, fontWeight: 700 }}>${parseFloat(a.final_price).toFixed(0)}</div>
                <Link to={`/pro/appointments/${a.id}`} style={{ color: '#444', fontSize: '11px', textDecoration: 'none' }}>View →</Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h2 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '14px' }}>Quick Actions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[
            { icon: '', label: 'New Appointment', href: '/pro/book' },
            { icon: '', label: 'View Clients', href: '/pro/clients' },
            { icon: '', label: 'Social Media', href: '/pro/social' },
            { icon: '', label: 'Analytics', href: '/pro/analytics' },
            { icon: '', label: 'Messages', href: '/pro/messages' },
          ].map(({ icon, label, href }) => (
            <Link key={label} to={href} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: '8px', textDecoration: 'none', color: '#ccc', fontSize: '13px', fontWeight: 600 }}>
              <span>{icon}</span> {label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── SERVICES TAB ─── */
function ServicesTab() {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null); // service id being edited
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({});
  const [newForm, setNewForm] = useState({ name: '', description: '', duration_minutes: 30, price: '', after_hours_price: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { loadServices(); }, []);

  const loadServices = async () => {
    try {
      const r = await api.get('/services/all');
      setServices(r.data);
    } catch (e) { console.error(e); }
  };

  const startEdit = (svc) => { setEditing(svc.id); setForm({ ...svc }); };
  const cancelEdit = () => { setEditing(null); setForm({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/services/${editing}`, form);
      setMsg('Saved');
      setEditing(null);
      loadServices();
    } catch (e) { setMsg('Error saving'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 2500);
  };

  const toggleActive = async (svc) => {
    try {
      await api.put(`/services/${svc.id}`, { ...svc, active: !svc.active });
      loadServices();
    } catch (e) { console.error(e); }
  };

  const addService = async () => {
    if (!newForm.name || !newForm.price) return;
    setSaving(true);
    try {
      await api.post('/services', newForm);
      setMsg('Service added');
      setAdding(false);
      setNewForm({ name: '', description: '', duration_minutes: 30, price: '', after_hours_price: '' });
      loadServices();
    } catch (e) { setMsg('Error adding service'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 2500);
  };

  return (
    <div style={{ maxWidth: '720px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>Your Services</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {msg && <span style={{ color: GREEN, fontSize: '12px', fontWeight: 600 }}>{msg}</span>}
          <button onClick={() => setAdding(true)} style={{ padding: '8px 16px', background: ACCENT, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
            + Add Service
          </button>
        </div>
      </div>

      {/* ADD FORM */}
      {adding && (
        <div style={{ background: SURFACE, border: `1px solid rgba(43,127,255,0.3)`, borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '14px', color: ACCENT }}>New Service</div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <input placeholder="Service name *" value={newForm.name} onChange={e => setNewForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
            <input placeholder="Price ($) *" type="number" value={newForm.price} onChange={e => setNewForm(p => ({ ...p, price: e.target.value }))} style={inputStyle} />
            <input placeholder="After-hours ($)" type="number" value={newForm.after_hours_price} onChange={e => setNewForm(p => ({ ...p, after_hours_price: e.target.value }))} style={inputStyle} />
            <input placeholder="Duration (min)" type="number" value={newForm.duration_minutes} onChange={e => setNewForm(p => ({ ...p, duration_minutes: e.target.value }))} style={inputStyle} />
          </div>
          <input placeholder="Description (optional)" value={newForm.description} onChange={e => setNewForm(p => ({ ...p, description: e.target.value }))} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '12px' }} />
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={addService} disabled={saving} style={{ padding: '8px 20px', background: ACCENT, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
              {saving ? 'Saving...' : 'Add'}
            </button>
            <button onClick={() => setAdding(false)} style={{ padding: '8px 16px', background: 'transparent', color: OFF_WHITE, border: `1px solid ${BORDER}`, borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* SERVICE LIST */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {services.map(svc => (
          <div key={svc.id} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '14px 16px', opacity: svc.active ? 1 : 0.45 }}>
            {editing === svc.id ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} style={inputStyle} />
                  <input type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="Price" style={inputStyle} />
                  <input type="number" value={form.after_hours_price || ''} onChange={e => setForm(p => ({ ...p, after_hours_price: e.target.value }))} placeholder="After-hours" style={inputStyle} />
                  <input type="number" value={form.duration_minutes} onChange={e => setForm(p => ({ ...p, duration_minutes: e.target.value }))} placeholder="Mins" style={inputStyle} />
                </div>
                <input value={form.description || ''} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description" style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', marginBottom: '10px' }} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={saveEdit} disabled={saving} style={{ padding: '6px 16px', background: ACCENT, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}>{saving ? 'Saving...' : 'Save'}</button>
                  <button onClick={cancelEdit} style={{ padding: '6px 12px', background: 'transparent', color: OFF_WHITE, border: `1px solid ${BORDER}`, borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '14px' }}>{svc.name}</div>
                  <div style={{ color: '#555', fontSize: '11px', marginTop: '2px' }}>
                    {svc.duration_minutes} min
                    {svc.after_hours_price && ` · After-hours $${svc.after_hours_price}`}
                    {svc.description && ` · ${svc.description}`}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ color: ACCENT, fontWeight: 800, fontSize: '15px' }}>${svc.price}</span>
                  <button onClick={() => startEdit(svc)} style={ghostBtn}>Edit</button>
                  <button onClick={() => toggleActive(svc)} style={{ ...ghostBtn, color: svc.active ? '#ef4444' : GREEN, borderColor: svc.active ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)' }}>
                    {svc.active ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── PROFILE TAB ─── */
function ProfileTab() {
  const [profile, setProfile] = useState({ name: '', bio: '', specialty: '', tagline: '', instagram: '', location: '', booking_note: '' });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/settings').then(r => {
      const p = r.data.profile || {};
      setProfile(prev => ({ ...prev, ...p }));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings/profile', { value: profile });
      setMsg('Profile saved');
    } catch (e) { setMsg('Error saving'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 2500);
  };

  if (!loaded) return <div style={{ color: OFF_WHITE, fontSize: '13px' }}>Loading...</div>;

  const fields = [
    { key: 'name', label: 'Display Name', placeholder: 'e.g. Christian Flair' },
    { key: 'specialty', label: 'Specialty / Title', placeholder: 'e.g. Master Barber · Precision Cuts' },
    { key: 'tagline', label: 'Tagline', placeholder: 'e.g. Where style meets precision' },
    { key: 'location', label: 'Location', placeholder: 'e.g. Philadelphia, PA' },
    { key: 'instagram', label: 'Instagram Handle', placeholder: '@christiangflair' },
    { key: 'booking_note', label: 'Booking Note', placeholder: 'e.g. DM to confirm same-day appointments' },
  ];

  return (
    <div style={{ maxWidth: '600px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>My Profile</h2>
        {msg && <span style={{ color: GREEN, fontSize: '12px', fontWeight: 600 }}>{msg}</span>}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
        {fields.map(({ key, label, placeholder }) => (
          <div key={key}>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: OFF_WHITE, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>{label}</label>
            <input
              value={profile[key] || ''}
              onChange={e => setProfile(p => ({ ...p, [key]: e.target.value }))}
              placeholder={placeholder}
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
        ))}

        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, color: OFF_WHITE, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>Bio</label>
          <textarea
            value={profile.bio || ''}
            onChange={e => setProfile(p => ({ ...p, bio: e.target.value }))}
            placeholder="Tell clients who you are, your style, your experience..."
            rows={4}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.6 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <button onClick={save} disabled={saving} style={{ padding: '12px 28px', background: ACCENT, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
          {saving ? 'Saving...' : 'Save Profile'}
        </button>
        <Link to="/pro/profile" style={{ fontSize: '13px', color: OFF_WHITE, textDecoration: 'none' }}>
          Preview my page →
        </Link>
      </div>
    </div>
  );
}

/* ─── CLIENTS TAB ─── */
function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/clients').then(r => { setClients(r.data.slice(0, 20)); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: OFF_WHITE, fontSize: '13px' }}>Loading clients...</div>;

  return (
    <div style={{ maxWidth: '720px' }}>
      <h2 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px' }}>Clients</h2>
      {clients.length === 0 ? (
        <div style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '28px', textAlign: 'center', color: '#444' }}>
          No clients yet. They'll appear here after their first booking.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {clients.map(c => (
            <div key={c.id} style={{ background: CARD, border: `1px solid ${BORDER}`, borderRadius: '10px', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: '14px' }}>{c.name}</div>
                <div style={{ color: '#555', fontSize: '11px', marginTop: '2px' }}>{c.phone || c.email || 'No contact'}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ color: ACCENT, fontWeight: 700, fontSize: '13px' }}>{c.visit_count || 0} visits</div>
                {c.total_spent > 0 && <div style={{ color: '#555', fontSize: '11px' }}>${parseFloat(c.total_spent).toFixed(0)} total</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── SHARED STYLES ─── */
const inputStyle = {
  background: '#0a1020',
  border: `1px solid ${BORDER}`,
  borderRadius: '8px',
  padding: '10px 12px',
  color: '#fff',
  fontSize: '13px',
  outline: 'none',
};

const ghostBtn = {
  padding: '5px 12px',
  background: 'transparent',
  color: OFF_WHITE,
  border: `1px solid ${BORDER}`,
  borderRadius: '6px',
  fontSize: '11px',
  fontWeight: 600,
  cursor: 'pointer',
};

/* ─── SOCIAL MEDIA TAB ─── */
function SocialMediaTab() {
  const [tab, setTab] = useState('posting');

  return (
    <div style={{ maxWidth: '100%' }}>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', borderBottom: `1px solid ${BORDER}`, paddingBottom: '12px' }}>
        <button
          onClick={() => setTab('posting')}
          style={{
            background: 'transparent',
            color: tab === 'posting' ? '#fff' : '#555',
            border: 'none',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            paddingBottom: '8px',
            borderBottom: tab === 'posting' ? `2px solid ${ACCENT}` : '2px solid transparent',
          }}
        >
          Schedule Posts
        </button>
        <button
          onClick={() => setTab('buffer')}
          style={{
            background: 'transparent',
            color: tab === 'buffer' ? '#fff' : '#555',
            border: 'none',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            paddingBottom: '8px',
            borderBottom: tab === 'buffer' ? `2px solid ${ACCENT}` : '2px solid transparent',
          }}
        >
          Buffer Integration
        </button>
      </div>

      {tab === 'posting' && <SocialPosting />}
      {tab === 'buffer' && <BufferIntegration />}
    </div>
  );
}
